import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { API } from '@/App';
import MarkdownContent from '@/components/MarkdownContent';

/**
 * Shared renderer for every public legal document page.
 * Shows the document content plus a "Last updated · vX.X" footer.
 */
const LegalDocPage = ({ documentType, title }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/legal/${documentType}`);
        if (active) setDoc(res.data);
      } catch (error) {
        console.error(`Failed to load ${documentType}`);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [documentType]);

  if (loading) {
    return <div className="text-center py-20 text-white">Loading...</div>;
  }

  const version = doc?.version != null && !Number.isNaN(parseFloat(doc.version))
    ? String(Math.round(parseFloat(doc.version) * 10) / 10)
    : null;
  const lastUpdated = doc?.last_updated ? new Date(doc.last_updated) : null;

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid={`legal-page-${documentType}`}>
      <Card className="glass-effect border-cyan-500/30">
        <CardHeader>
          <CardTitle className="text-3xl" style={{ color: '#331a67', fontFamily: "'Cormorant Garamond', serif" }}>
            {doc?.title || title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MarkdownContent content={doc?.content} />
          {(version || lastUpdated) && (
            <div
              className="mt-8 pt-4 border-t border-gray-300 text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1"
              data-testid={`legal-footer-${documentType}`}
            >
              {lastUpdated && (
                <span>
                  Last updated · {lastUpdated.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              )}
              {version && <span>Version v{version}</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalDocPage;
